import Field from "@farmact/model/src/model/Field";
import { Machine } from "@farmact/model/src/model/Machine";
import { TaskRecord } from "@farmact/model/src/model/Order";
import { TaskRecordType } from "@farmact/model/src/model/services/TaskRecords";
import { TimeTracking } from "@farmact/model/src/model/TimeTracking";
import { Track } from "@farmact/model/src/model/Track";
import { OrderWorkType } from "@farmact/model/src/model/workTypes/OrderWorkType";
import dayjs from "dayjs";
import duration, { DurationUnitType } from "dayjs/plugin/duration";
import { computeLength } from "spherical-geometry-js";
import { ServicePriceUnit } from "@farmact/model/src/model/services/Service";

dayjs.extend(duration);

export function calculateAreaInHectars(fields: Field[]): number {
    return fields.reduce((prev, curr) => prev + curr.areaHa, 0);
}

/**
 * Calculate the length of the given tracks in meter
 * @param tracks
 * @returns
 */
export function calculateLength(tracks: Track[]): number {
    return tracks.reduce((acc, cur) => acc + computeLength(cur.shape), 0);
}

export function calculateLengthTaskRecordsInMeters(taskRecords: TaskRecord[]) {
    return taskRecords
        .filter(taskRecord => taskRecord.type === TaskRecordType.TRACKS)
        .reduce((acc, curr) => {
            if (!curr.record) {
                return acc;
            }
            if (curr.unit === ServicePriceUnit.KILOMETER) {
                return acc + curr.record * 1000;
            } else if (curr.unit === ServicePriceUnit.METER) {
                return acc + curr.record;
            }
            return acc;
        }, 0);
}

export function calculateTonsTaskRecords(taskRecords: TaskRecord[]): number {
    return taskRecords
        .filter(taskRecord => taskRecord.type === TaskRecordType.TONS)
        .reduce((acc, curr) => acc + (curr.record ?? 0), 0);
}

export function calculateCubicMetersTaskRecords(taskRecords: TaskRecord[]): number {
    return taskRecords
        .filter(taskRecord => taskRecord.type === TaskRecordType.CUBIC_METERS)
        .reduce((acc, curr) => acc + (curr.record ?? 0), 0);
}

export function calculateLiterTaskRecords(taskRecords: TaskRecord[]): number {
    return taskRecords
        .filter(taskRecord => taskRecord.type === TaskRecordType.LITER)
        .reduce((acc, curr) => acc + (curr.record ?? 0), 0);
}

function calculateCostForSingleMachine(machine: Machine, usageTimeHours: number, usageAreaHa: number): number {
    const hectarCost = usageAreaHa * machine.costPerHectar;
    const usageCost = usageTimeHours * machine.costPerUsageHour;
    // TODO
    const drivingCost = 0;
    return hectarCost + usageCost + drivingCost;
}

// FIXME not sure if every used machine should be multiplied by the total hour/area usage
export function calculateMachineCost(machines: Machine[], usageTimeHours: number, usageAreaHa: number): number {
    return machines
        .map(machine => {
            return calculateCostForSingleMachine(machine, usageTimeHours, usageAreaHa);
        })
        .reduce((prev, curr) => prev + curr, 0);
}

/**
 * Calculate the total tracked time spend for the given `workType`
 * @param timeTrackings
 * @param workType
 * @returns Tracked time in minutes
 */
export function calculateTimeTrackingAmountByWorkType(timeTrackings: TimeTracking[], workType: OrderWorkType): number {
    const trackings = timeTrackings.filter(timeTracking => timeTracking.orderWorkType === workType);
    return calculateTimeTrackingAmount(trackings, "minutes");
}

/**
 * Calculate the total tracked time
 * @param timeTrackings
 * @param unit
 * @returns
 */
export function calculateTimeTrackingAmount(timeTrackings: TimeTracking[], unit: DurationUnitType = "minutes"): number {
    const timeInSeconds = timeTrackings.reduce((acc, cur) => {
        const diff = Math.abs(dayjs(cur.startDateTime).diff(cur.endDateTime, "seconds"));
        return acc + diff;
    }, 0);
    return dayjs.duration(timeInSeconds, "seconds").as(unit);
}
